import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { submitJobApplication } from '../utils/formSubmission';
import BotPrevention from './BotPrevention';

// Animations
const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideIn = keyframes`
  from { transform: translateY(20px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`;

// Modal Container
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
  animation: ${fadeIn} 0.3s ease;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
`;

const ModalContent = styled.div`
  background-color: white;
  border-radius: 10px;
  max-width: 500px;
  width: 100%;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  animation: ${slideIn} 0.4s ease;
  overflow: hidden;
  position: relative;
  margin: auto;
  
  @media (max-width: 768px) {
    margin: 1rem auto;
    max-height: calc(100vh - 2rem);
    overflow-y: auto;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 15px;
  right: 15px;
  background: transparent;
  border: none;
  font-size: 1.5rem;
  color: #999;
  cursor: pointer;
  transition: color 0.2s ease;
  z-index: 10;
  
  &:hover {
    color: #333;
  }
`;

const ModalHeader = styled.div`
  padding: 1.75rem 2rem 0;
  text-align: center;
`;

const ModalTitle = styled.h3`
  font-size: 1.6rem;
  color: #333;
  margin-bottom: 0.25rem;
`;

const StepIndicatorContainer = styled.div`
  display: flex;
  justify-content: center;
  margin: 1.5rem 0;
`;

const StepLine = styled.div`
  height: 3px;
  background-color: #eee;
  margin: 0 0.5rem;
  flex-grow: 1;
  max-width: 50px;
  border-radius: 3px;
  transition: background-color 0.3s ease;
`;

const ActiveStepLine = styled(StepLine)`
  background-color: var(--primary-color);
`;

const ModalBody = styled.div`
  padding: 0 2rem 2rem;
`;

const FormStep = styled.div`
  animation: ${fadeIn} 0.4s ease;
`;

const InputLabel = styled.label`
  display: block;
  font-size: 1rem;
  font-weight: 500;
  margin-bottom: 0.5rem;
  color: #444;
`;

const InputField = styled.input`
  width: 100%;
  padding: 0.9rem 1rem;
  font-size: 1rem;
  border: 1px solid #ddd;
  border-radius: 6px;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(255, 107, 0, 0.1);
  }
`;

const SelectField = styled.select`
  width: 100%;
  padding: 0.9rem 1rem;
  font-size: 1rem;
  border: 1px solid #ddd;
  border-radius: 6px;
  transition: all 0.3s ease;
  background-color: white;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(255, 107, 0, 0.1);
  }
`;

const TextAreaField = styled.textarea`
  width: 100%;
  padding: 0.9rem 1rem;
  font-size: 1rem;
  border: 1px solid #ddd;
  border-radius: 6px;
  transition: all 0.3s ease;
  min-height: 120px;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(255, 107, 0, 0.1);
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 2rem;
`;

const ButtonBase = styled.button`
  padding: 0.8rem 1.5rem;
  border-radius: 6px;
  font-weight: 500;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.3s ease;
`;

const PrimaryButton = styled(ButtonBase)`
  background-color: var(--primary-color);
  color: white;
  border: none;
  
  &:hover {
    background-color: #e05e00;
  }
`;

const SecondaryButton = styled(ButtonBase)`
  background-color: white;
  color: #444;
  border: 1px solid #ddd;
  
  &:hover {
    background-color: #f5f5f5;
  }
`;

const StepDescription = styled.p`
  text-align: center;
  color: #444;
  margin-bottom: 2rem;
  font-size: 0.95rem;
`;

const SuccessMessage = styled.div`
  text-align: center;
  padding: 2rem 1rem;
  
  svg {
    width: 70px;
    height: 70px;
    color: var(--primary-color);
    margin-bottom: 1rem;
  }
  
  h4 {
    font-size: 1.6rem;
    margin-bottom: 1rem;
    color: #333;
  }
  
  p {
    color: #444;
    margin-bottom: 2rem;
  }
`;

// Success Icon
const SuccessIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M22 11.08V12C21.9988 14.1564 21.3005 16.2547 20.0093 17.9818C18.7182 19.709 16.9033 20.9725 14.8354 21.5839C12.7674 22.1953 10.5573 22.1219 8.53447 21.3746C6.51168 20.6273 4.78465 19.2461 3.61096 17.4371C2.43727 15.628 1.87979 13.4881 2.02168 11.3363C2.16356 9.18457 2.99721 7.13633 4.39828 5.49707C5.79935 3.85782 7.69279 2.71538 9.79619 2.24015C11.8996 1.76491 14.1003 1.98234 16.07 2.86" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M22 4L12 14.01L9 11.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const HoneypotInput = styled.input`
  display: none;
`;

interface FormData {
  name: string;
  email: string;
  position: string;
  experience: string;
  skills: string;
  motivation: string;
}

interface JoinTeamModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const JoinTeamModal: React.FC<JoinTeamModalProps> = ({ isOpen, onClose }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    position: '',
    experience: '',
    skills: '',
    motivation: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isHumanVerified, setIsHumanVerified] = useState(false);
  const [honeypot, setHoneypot] = useState('');
  const inputRef = useRef<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  
  const handleValidationChange = (isValid: boolean) => {
    setIsHumanVerified(isValid);
  };
  
  const scrollToTop = () => {
    if (modalRef.current) {
      modalRef.current.scrollTop = 0;
    }
  };
  
  useEffect(() => {
    scrollToTop();
  }, [currentStep]);
  
  useEffect(() => {
    if (isOpen && inputRef.current && !isSubmitted) {
      inputRef.current.focus();
      
      // Add slight delay to ensure input is visible after keyboard appears
      setTimeout(() => {
        inputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 400);
    }
  }, [currentStep, isOpen, isSubmitted]);
  
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);
  
  if (!isOpen) return null;
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  const nextStep = () => {
    setCurrentStep(prev => prev + 1);
  };
  
  const prevStep = () => {
    setCurrentStep(prev => prev - 1);
  };
  
  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    
    if (honeypot) {
      console.log('Bot detected via honeypot');
      setIsSubmitted(true);
      return;
    }
    
    if (!isHumanVerified) {
      setSubmitError('Human verification failed. Please try again.');
      return;
    }
    
    if (currentStep === 6 && formData.motivation) {
      try {
        setIsSubmitting(true);
        setSubmitError(null);
        
        const result = await submitJobApplication(formData);
        
        if (result.success) {
          console.log('Form submitted successfully:', result);
          setIsSubmitted(true);
        } else {
          setSubmitError(result.message);
        }
      } catch (error) {
        console.error('Error submitting form:', error);
        setSubmitError('An unexpected error occurred. Please try again.');
      } finally {
        setIsSubmitting(false);
      }
    } 
    else if (
      (currentStep === 1 && formData.name) ||
      (currentStep === 2 && formData.email) ||
      (currentStep === 3 && formData.position) ||
      (currentStep === 4 && formData.experience) ||
      (currentStep === 5 && formData.skills)
    ) {
      nextStep();
    }
  };
  
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };
  
  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <form onSubmit={handleSubmit}>
            <FormStep>
              <StepDescription>
                Let's start with your name
              </StepDescription>
              <InputLabel htmlFor="name">Full Name</InputLabel>
              <InputField
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                ref={inputRef as React.RefObject<HTMLInputElement>}
                placeholder="John Doe"
                required
              />
              <ButtonContainer>
                <SecondaryButton type="button" onClick={onClose}>
                  Cancel
                </SecondaryButton>
                <PrimaryButton
                  type="submit"
                  disabled={!formData.name}
                >
                  Next
                </PrimaryButton>
              </ButtonContainer>
            </FormStep>
          </form>
        );
        
      case 2:
        return (
          <form onSubmit={handleSubmit}>
            <FormStep>
              <StepDescription>
                How can we contact you?
              </StepDescription>
              <InputLabel htmlFor="email">Email Address</InputLabel>
              <InputField
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                ref={inputRef as React.RefObject<HTMLInputElement>}
                placeholder="your.email@example.com"
                required
              />
              <ButtonContainer>
                <SecondaryButton type="button" onClick={prevStep}>
                  Back
                </SecondaryButton>
                <PrimaryButton
                  type="submit"
                  disabled={!formData.email}
                >
                  Next
                </PrimaryButton>
              </ButtonContainer>
            </FormStep>
          </form>
        );
        
      case 3:
        return (
          <form onSubmit={handleSubmit}>
            <FormStep>
              <StepDescription>
                What position are you interested in?
              </StepDescription>
              <InputLabel htmlFor="position">Position</InputLabel>
              <SelectField
                id="position"
                name="position"
                value={formData.position}
                onChange={handleInputChange}
                ref={inputRef as React.RefObject<HTMLSelectElement>}
                required
              >
                <option value="">Select a position</option>
                <option value="Frontend Developer">Frontend Developer</option>
                <option value="Backend Developer">Backend Developer</option>
                <option value="Full Stack Developer">Full Stack Developer</option>
                <option value="UI/UX Designer">UI/UX Designer</option>
                <option value="Product Manager">Product Manager</option>
                <option value="Project Manager">Project Manager</option>
                <option value="QA Engineer">QA Engineer</option>
                <option value="DevOps Engineer">DevOps Engineer</option>
                <option value="Other">Other</option>
              </SelectField>
              <ButtonContainer>
                <SecondaryButton type="button" onClick={prevStep}>
                  Back
                </SecondaryButton>
                <PrimaryButton
                  type="submit"
                  disabled={!formData.position}
                >
                  Next
                </PrimaryButton>
              </ButtonContainer>
            </FormStep>
          </form>
        );
        
      case 4:
        return (
          <form onSubmit={handleSubmit}>
            <FormStep>
              <StepDescription>
                How much experience do you have?
              </StepDescription>
              <InputLabel htmlFor="experience">Experience Level</InputLabel>
              <SelectField
                id="experience"
                name="experience"
                value={formData.experience}
                onChange={handleInputChange}
                ref={inputRef as React.RefObject<HTMLSelectElement>}
                required
              >
                <option value="">Select your experience</option>
                <option value="0-1">0-1 years</option>
                <option value="1-3">1-3 years</option>
                <option value="3-5">3-5 years</option>
                <option value="5-7">5-7 years</option>
                <option value="7-10">7-10 years</option>
                <option value="10+">10+ years</option>
              </SelectField>
              <ButtonContainer>
                <SecondaryButton type="button" onClick={prevStep}>
                  Back
                </SecondaryButton>
                <PrimaryButton
                  type="submit"
                  disabled={!formData.experience}
                >
                  Next
                </PrimaryButton>
              </ButtonContainer>
            </FormStep>
          </form>
        );
      
      case 5:
        return (
          <form onSubmit={handleSubmit}>
            <FormStep>
              <StepDescription>
                What are your key skills?
              </StepDescription>
              <InputLabel htmlFor="skills">Key Skills</InputLabel>
              <InputField
                type="text"
                id="skills"
                name="skills"
                value={formData.skills}
                onChange={handleInputChange}
                ref={inputRef as React.RefObject<HTMLInputElement>}
                placeholder="e.g., JavaScript, React, Node.js"
                required
              />
              <ButtonContainer>
                <SecondaryButton type="button" onClick={prevStep}>
                  Back
                </SecondaryButton>
                <PrimaryButton
                  type="submit"
                  disabled={!formData.skills}
                >
                  Next
                </PrimaryButton>
              </ButtonContainer>
            </FormStep>
          </form>
        );
        
      case 6:
        return (
          <form onSubmit={handleSubmit}>
            <FormStep>
              <StepDescription>
                Why do you want to join our team?
              </StepDescription>
              <InputLabel htmlFor="motivation">Motivation</InputLabel>
              <TextAreaField
                id="motivation"
                name="motivation"
                value={formData.motivation}
                onChange={handleInputChange}
                ref={inputRef as React.RefObject<HTMLTextAreaElement>}
                placeholder="Tell us why you'd like to work with us..."
                required
              />
              <HoneypotInput
                type="text"
                name="website_field"
                value={honeypot}
                onChange={(e) => setHoneypot(e.target.value)}
                autoComplete="off"
              />
              <BotPrevention onValidationChange={handleValidationChange} formId="joinTeam" />
              {submitError && (
                <div style={{ color: 'red', marginTop: '10px', fontSize: '0.9rem' }}>
                  {submitError}
                </div>
              )}
              <ButtonContainer>
                <SecondaryButton type="button" onClick={prevStep}>
                  Back
                </SecondaryButton>
                <PrimaryButton
                  type="submit"
                  disabled={!formData.motivation || isSubmitting}
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Application'}
                </PrimaryButton>
              </ButtonContainer>
            </FormStep>
          </form>
        );
      
      default:
        return null;
    }
  };
  
  const renderStepIndicators = () => {
    const totalSteps = 6;
    const indicators = [];
    
    for (let i = 1; i <= totalSteps; i++) {
      if (i <= currentStep) {
        indicators.push(<ActiveStepLine key={i} />);
      } else {
        indicators.push(<StepLine key={i} />);
      }
    }
    
    return indicators;
  };
  
  return (
    <ModalOverlay onKeyDown={handleKeyDown} onClick={onClose}>
      <ModalContent ref={modalRef} onClick={e => e.stopPropagation()}>
        <CloseButton onClick={onClose}>&times;</CloseButton>
        
        <ModalHeader>
          <ModalTitle>Join Our Team</ModalTitle>
          {!isSubmitted && (
            <StepIndicatorContainer>
              {renderStepIndicators()}
            </StepIndicatorContainer>
          )}
        </ModalHeader>
        
        <ModalBody>
          {!isSubmitted ? (
            renderStep()
          ) : (
            <SuccessMessage>
              <SuccessIcon />
              <h4>Application Received!</h4>
              <p>Thank you for your interest in joining Uptrend Systems. We'll review your application and get back to you soon.</p>
              <PrimaryButton onClick={onClose}>
                Close
              </PrimaryButton>
            </SuccessMessage>
          )}
        </ModalBody>
      </ModalContent>
    </ModalOverlay>
  );
};

export default JoinTeamModal; 